import type { ErrorEvent } from '@sentry/react';

export const REDACTED = '[redacted]';

/**
 * `ClientError` builds its message as
 * `${extractMessage(response)}: ${JSON.stringify({ response, request })}`, and
 * `request` carries the operation variables — on the auth mutations that is the
 * user's plaintext password. Anything from the serialized blob onwards goes; the
 * human-readable prefix before it is the actual GraphQL error and is worth
 * keeping.
 */
const redactSerializedRequest = (value: string): string => {
  const start = value.indexOf('{"response":');
  return start === -1 ? value : `${value.slice(0, start)}${REDACTED}`;
};

/**
 * graphql-js writes variable coercion failures with the rejected value inlined —
 * `Variable "$password" got invalid value "hunter2"; ...`. Keeps the variable
 * name, which is the diagnostic part, and drops the value.
 *
 * Redacts to the end of the string rather than to the first `;`. The message is
 * built as `prefix + '; ' + error.message` (graphql/execution/values.js), and
 * that trailing half is the scalar's own complaint, which inspects the same
 * value again — `String cannot represent a non string value: "hunter2"`. So the
 * secret appears on both sides of the separator and stopping at it leaks the
 * second copy. The type name lost here is worth less than the guarantee.
 */
const redactCoercedVariableValues = (value: string): string =>
  value.replace(
    /(Variable "\$[^"]*" got invalid value )[\s\S]*/g,
    `$1${REDACTED}`
  );

const scrub = (value: string): string =>
  redactCoercedVariableValues(redactSerializedRequest(value));

/**
 * Strips request payloads out of an outgoing Sentry event.
 *
 * The call sites are careful not to hand Sentry a `ClientError` (see
 * `query-client.ts`), but this is the backstop for the ones that can't be:
 * `handleError` in `gql-client.ts` captures raw GraphQL errors off the
 * websocket, Sentry's own `linkedErrorsIntegration` walks any `cause` chain a
 * future caller attaches, and the global unhandled-rejection handler captures
 * whatever it is given. A leaked password is not the kind of thing that should
 * depend on every call site remembering.
 *
 * Every entry in `exception.values` is scrubbed, not just the first — that array
 * is where the linked-errors chain lands.
 */
export const scrubSentryEvent = (event: ErrorEvent): ErrorEvent => {
  if (event.message) {
    event.message = scrub(event.message);
  }

  event.exception?.values?.forEach((exception) => {
    if (exception.value) {
      exception.value = scrub(exception.value);
    }
  });

  return event;
};
